import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import inksolverLogo from "../assets/inksolver-logo.png";

const OPTIONS = [
  {
    key: "productGuidance",
    title: "Study guidance",
    description: "Helpful nudges for onboarding, practice, and getting more from your slate."
  },
  {
    key: "progressSummaries",
    title: "Progress and streaks",
    description: "Occasional summaries that help you notice consistency and areas to revisit."
  },
  {
    key: "collaborationInvitations",
    title: "Ink Together invitations",
    description: "Email alerts when another learner invites you into a shared study room."
  }
];

function EmailPreferencesPage({ apiBase, authToken, signedIn }) {
  const [searchParams] = useSearchParams();
  const preferenceToken = searchParams.get("token") || "";
  const [state, setState] = useState(null);
  const [draft, setDraft] = useState({});
  const [status, setStatus] = useState("loading");
  const [message, setMessage] = useState("");

  const endpoint = preferenceToken
    ? `${apiBase}/api/communications/preferences?token=${encodeURIComponent(preferenceToken)}`
    : `${apiBase}/api/me/communication-preferences`;

  useEffect(() => {
    if (!preferenceToken && !authToken) {
      setStatus("auth_required");
      return undefined;
    }
    const controller = new AbortController();
    setStatus("loading");
    fetch(endpoint, {
      headers: authToken && !preferenceToken ? { Authorization: `Bearer ${authToken}` } : {},
      signal: controller.signal
    })
      .then(async (response) => {
        const payload = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(payload.error || "We could not load your email preferences.");
        setState(payload);
        setDraft(payload.preferences || {});
        setStatus("ready");
      })
      .catch((error) => {
        if (error.name !== "AbortError") {
          setMessage(error.message || "We could not load your email preferences.");
          setStatus("error");
        }
      });
    return () => controller.abort();
  }, [authToken, endpoint, preferenceToken]);

  async function savePreferences(nextDraft = draft, unsubscribeAll = false) {
    setStatus("saving");
    setMessage("");
    try {
      const response = await fetch(endpoint, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          ...(authToken && !preferenceToken ? { Authorization: `Bearer ${authToken}` } : {})
        },
        body: JSON.stringify(unsubscribeAll ? { unsubscribeAll: true } : { preferences: nextDraft })
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.error || "We could not save your preferences.");
      setState(payload);
      setDraft(payload.preferences || {});
      setStatus("ready");
      setMessage(unsubscribeAll ? "You have been unsubscribed from optional emails." : "Your email preferences are saved.");
    } catch (error) {
      setMessage(error.message || "We could not save your preferences.");
      setStatus("error");
    }
  }

  return (
    <main className="email-preferences-page">
      <section className="email-preferences-card" aria-labelledby="email-preferences-title">
        <header className="email-preferences-header">
          <img src={inksolverLogo} alt="InkSolver" />
          <div className="email-preferences-kicker">YOUR INBOX, YOUR CHOICE</div>
          <h1 id="email-preferences-title">Email preferences</h1>
          <p>Choose the optional updates that are useful to you. Account and security emails always stay on.</p>
          {state?.email && <span className="email-preferences-address">For {state.email}</span>}
        </header>

        <div className="email-preferences-body">
          {status === "loading" && <div className="email-preferences-state">Loading your choices...</div>}
          {status === "auth_required" && (
            <div className="email-preferences-state">
              Sign in to manage your email preferences. <Link to="/login">Go to login</Link>
            </div>
          )}
          {status === "error" && !state && <div className="email-preferences-alert error">{message}</div>}

          {state && (
            <>
              {state.suppressed && (
                <div className="email-preferences-alert error">
                  Delivery to this address has been paused after a provider rejection. Contact support after fixing the mailbox issue.
                </div>
              )}
              <div className="email-preferences-options">
                {OPTIONS.map((option) => (
                  <label className="email-preference-option" key={option.key}>
                    <span>
                      <strong>{option.title}</strong>
                      <small>{option.description}</small>
                    </span>
                    <input
                      type="checkbox"
                      checked={draft[option.key] !== false}
                      disabled={status === "saving" || state.suppressed}
                      onChange={(event) => setDraft((current) => ({ ...current, [option.key]: event.target.checked }))}
                    />
                  </label>
                ))}
                <div className="email-preference-option fixed">
                  <span>
                    <strong>Account and security</strong>
                    <small>Verification, password, privacy, and essential service notices.</small>
                  </span>
                  <span className="email-preference-required">Always on</span>
                </div>
              </div>

              {message && <div className={`email-preferences-alert ${status === "error" ? "error" : "success"}`}>{message}</div>}
              <div className="email-preferences-actions">
                <button
                  type="button"
                  className="email-preferences-save"
                  disabled={status === "saving" || state.suppressed}
                  onClick={() => savePreferences()}
                >
                  {status === "saving" ? "Saving..." : "Save preferences"}
                </button>
                <button
                  type="button"
                  className="email-preferences-unsubscribe"
                  disabled={status === "saving"}
                  onClick={() => savePreferences({}, true)}
                >
                  Unsubscribe from optional emails
                </button>
              </div>
            </>
          )}
        </div>

        <footer className="email-preferences-footer">
          <Link to={signedIn ? "/profile" : "/login"}>{signedIn ? "Back to profile" : "Back to InkSolver"}</Link>
          <a href="mailto:support@inksolver.com">Need help?</a>
        </footer>
      </section>
    </main>
  );
}

export default EmailPreferencesPage;
